import React, { useContext, useEffect, useState } from "react";
import SectionHeader from "../components/section/SectionHeader";
import {
  FIXED_NETWORK_ELEMENT_FILTER_COLUMNS,
  FIXED_NETWORK_ELEMENT_TABLE_HEADERS,
  VIEW_FIXED_NETWORK_ELEMENT_FORM_FIELDS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  transformFixedNetworkElementData,
  transformFixedNetworkElementResponseRow,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import TableActionButton from "../ui/TableActionButton";
import { getFixedNetworkElementDataByRef } from "../api";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import ViewRow from "../components/dataTable/ViewRow";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import OrgContext from "../store/orgContext";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import InfoAlert from "../ui/InfoAlert";
import { READ_FIXED_NETWORK_ELEMENT_TRANSLATIONS } from "../translations/util";
import SearchContext from "../store/searchContext";

interface ISearchData {
  RFSS?: string | null;
  "TARGET-ID"?: string | null;
}

const INITIAL_STATE = {
  RFSS: "",
  "TARGET-ID": "",
};

const searchFields = ["RFSS", "TARGET-ID"];

const searchConfigNew = createSearchConfig(
  searchFields,
  VIEW_FIXED_NETWORK_ELEMENT_FORM_FIELDS
);

const FixedNetworkElement = () => {
  const { token } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  useAppTitle(
    "P25 Supp. Service Reg. (FNE) - IWF Config Admin Tool (ICAT) by AT&T"
  );
  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [fixedNetworkData, setFixedNetworkData] = useState<any[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
  }>({
    data: {},
    pageSize: 20,
    pageNum: 1,
  });

  const fetchFixedNetworkElementData = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize } = searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(data &&
            data.RFSS && {
              RFSS: data.RFSS,
            }),
          ...(data &&
            data["TARGET-ID"] && {
              "TARGET-ID": data["TARGET-ID"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/realtime/p25/ss-reg-fne?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setFixedNetworkData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchFixedNetworkElementData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedOrg]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  return (
    <>
      <SectionHeader
        pageTitle={READ_FIXED_NETWORK_ELEMENT_TRANSLATIONS.header}
        pageDescription={READ_FIXED_NETWORK_ELEMENT_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          {showSidePanel && selectedOrg && selectedConnectionId ? (
            <ViewRow
              panelName={
                READ_FIXED_NETWORK_ELEMENT_TRANSLATIONS.viewRow.panelName
              }
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              formFields={VIEW_FIXED_NETWORK_ELEMENT_FORM_FIELDS}
              transformResponseRow={transformFixedNetworkElementResponseRow}
              loadDataByRef={getFixedNetworkElementDataByRef}
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            showTableControls
            filterColumns={FIXED_NETWORK_ELEMENT_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            showCreateButton={false}
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            rows={transformFixedNetworkElementData(fixedNetworkData).map(
              (row: { id: string }) => ({
                ...row,
                action: (
                  <TableActionButton
                    onActionButtonClick={() => {
                      setSelectedConnectionId(row.id);
                      handleToggleSidePanel();
                    }}
                  />
                ),
              })
            )}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={FIXED_NETWORK_ELEMENT_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_FIXED_NETWORK_ELEMENT_TRANSLATIONS.infoAlert.heading}
          subText={READ_FIXED_NETWORK_ELEMENT_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default FixedNetworkElement;
